import React, { useCallback, useMemo, useRef, useState } from 'react';
import AdminPanel from './AdminPanel';
import LoadingSpinner from './components/LoadingSpinner';
import ProductCard from './components/ProductCard';
import Cart from './components/Cart';
import CheckoutForm, { emptyForm as emptyCheckoutForm } from './components/CheckoutForm';
import OrderTracking from './components/OrderTracking';
import LoginModal from './components/LoginModal';
import EmployeePortalModal from './components/EmployeePortalModal';
import ProductDetailModal from './components/ProductDetailModal';
import { useProducts } from './hooks/useProducts';
import { useCart } from './hooks/useCart';
import { useAuth, getAuthErrorMessage } from './hooks/useAuth';
import { fetchProductById } from './api/products';
import { stockSearchScore } from './utils/stockAvailability';
import { createOrder, trackOrder, fetchOrderHistory } from './api/orders';
import { lookupCustomerByPhone } from './api/customers';
import { getErrorMessage } from './api/client';
import { API_BASE_URL } from './config';
import StoreHero from './components/storefront/StoreHero';
import AnnouncementBar from './components/storefront/AnnouncementBar';
import StoreTopHeader from './components/storefront/StoreTopHeader';
import StoreMainNav from './components/storefront/StoreMainNav';
import CategoryShowcase from './components/storefront/CategoryShowcase';
import FloatingChatButton from './components/storefront/FloatingChatButton';
import StorefrontMediaLayout from './components/storefront/StorefrontMediaLayout';

const CATEGORY_FILTERS = [
  'All',
  'Computer Parts',
  'PisoWiFi/PisoNet',
  'CCTV Products',
  'Fiber Products',
  'Solar Products'
];

const FEATURED_COUNT = 5;
function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function App() {
  const featuredSectionRef = useRef(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showEmployeePortal, setShowEmployeePortal] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const {
    products,
    isLoading: isLoadingProducts,
    isRefreshing: isRefreshingProducts,
    isSlow: isLoadingProductsSlow,
    error: productsError,
    refetch: refetchProducts,
    fromCache: productsFromCache,
  } = useProducts(API_BASE_URL, { limit: FEATURED_COUNT, fields: 'card' });

  const cart = useCart();
  const handleAuthVerifySuccess = useCallback(() => {
    setShowAdmin(true);
  }, []);
  const handleAuthLoginSuccess = useCallback(() => {
    setShowAdmin(true);
    setShowLogin(false);
    setLoginForm({ username: '', password: '' });
    setLoginError('');
  }, []);
  const handleAuthLogout = useCallback((message) => {
    setShowAdmin(false);
    setLoginError(message || '');
  }, []);
  const auth = useAuth({
    onVerifySuccess: handleAuthVerifySuccess,
    onLoginSuccess: handleAuthLoginSuccess,
    onLogout: handleAuthLogout,
  });
  const { logout: logoutAdmin } = auth;

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [checkoutForm, setCheckoutForm] = useState(emptyCheckoutForm);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutSuccess, setCheckoutSuccess] = useState('');
  const [trackForm, setTrackForm] = useState({ orderId: '', phone: '' });
  const [trackResult, setTrackResult] = useState(null);
  const [historyResults, setHistoryResults] = useState([]);
  const [trackError, setTrackError] = useState('');
  const [isTrackingOrder, setIsTrackingOrder] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const backendUrl = API_BASE_URL;

  const handleLogout = useCallback(() => {
    logoutAdmin('');
  }, [logoutAdmin]);

  const handleAdminAuthExpired = useCallback(() => {
    logoutAdmin('Session expired. Please log in again.');
  }, [logoutAdmin]);

  const filteredProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const list = products.filter((product) => {
      const matchesCategory =
        selectedCategory === 'All' || product.category === selectedCategory;
      const matchesSearch =
        !term ||
        String(product.name || '').toLowerCase().includes(term) ||
        String(product.description || '').toLowerCase().includes(term) ||
        String(product.sku || '').toLowerCase().includes(term) ||
        String(product.barcode || '').toLowerCase().includes(term);
      return matchesCategory && matchesSearch;
    });
    if (!term) return list;
    return [...list].sort((a, b) => stockSearchScore(b) - stockSearchScore(a));
  }, [products, searchTerm, selectedCategory]);

  const featuredProducts = useMemo(() => {
    const term = searchTerm.trim();
    if (term) {
      return filteredProducts.slice(0, FEATURED_COUNT);
    }
    return shuffleArray(filteredProducts).slice(0, FEATURED_COUNT);
  }, [filteredProducts, searchTerm]);

  const productCounts = useMemo(() => {
    const byCategory = {};
    products.forEach((p) => {
      const c = p.category || 'Uncategorized';
      byCategory[c] = (byCategory[c] || 0) + 1;
    });
    return { all: products.length, byCategory };
  }, [products]);

  const scrollToFeatured = useCallback(() => {
    featuredSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const scrollToCart = useCallback(() => {
    document.getElementById('store-cart-sidebar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleLogin = useCallback(async () => {
    const { username, password } = loginForm;
    setLoginError('');
    if (!username || !password) {
      setLoginError('Username and password are required.');
      return;
    }
    try {
      setIsLoggingIn(true);
      await auth.login(username, password);
    } catch (err) {
      setLoginError(getAuthErrorMessage(err, 'Invalid credentials'));
    } finally {
      setIsLoggingIn(false);
    }
  }, [loginForm, auth]);

  const addToCart = useCallback(
    (product) => {
      setCheckoutSuccess('');
      cart.addItem(product);
    },
    [cart]
  );

  const updateCartQty = useCallback(
    (id, nextQty) => {
      setCheckoutSuccess('');
      cart.updateQty(id, nextQty);
    },
    [cart]
  );

  const openProductDetail = useCallback(
    async (productId) => {
      setIsDetailLoading(true);
      setDetailError('');
      setSelectedProduct(null);
      try {
        const data = await fetchProductById(productId);
        setSelectedProduct(data);
      } catch (err) {
        setDetailError(getErrorMessage(err, 'Failed to load product details.'));
      } finally {
        setIsDetailLoading(false);
      }
    },
    []
  );

  const handlePlaceOrder = useCallback(async () => {
    setCheckoutError('');
    setCheckoutSuccess('');
    if (cart.items.length === 0) {
      setCheckoutError('Your cart is empty.');
      return;
    }
    if (
      !checkoutForm.customerName ||
      !checkoutForm.customerPhone ||
      !checkoutForm.customerAddress
    ) {
      setCheckoutError('Name, phone, and address are required.');
      return;
    }
    try {
      setIsPlacingOrder(true);
      const payload = {
        customerName: checkoutForm.customerName,
        customerPhone: checkoutForm.customerPhone,
        customerAddress: checkoutForm.customerAddress,
        notes: checkoutForm.notes,
        paymentMethod: checkoutForm.paymentMethod,
        items: cart.items.map((item) => ({
          productId: item.id,
          name: item.name,
          price: item.price,
          qty: item.qty,
          image: item.image || '',
        })),
      };
      const response = await createOrder(payload);
      const checkoutUrl = response?.payment?.checkoutUrl;
      const paymentLabel =
        checkoutForm.paymentMethod === 'online' && checkoutUrl
          ? ` Online payment link (stub): ${checkoutUrl}`
          : '';
      setCheckoutSuccess(
        `Order placed successfully. Order ID: ${response.orderId}.${paymentLabel}`
      );
      cart.clearCart();
      setCheckoutForm(emptyCheckoutForm);
    } catch (err) {
      setCheckoutError(getErrorMessage(err, 'Failed to place order.'));
    } finally {
      setIsPlacingOrder(false);
    }
  }, [cart, checkoutForm]);

  const handleTrackOrder = useCallback(async () => {
    setTrackError('');
    setTrackResult(null);
    setHistoryResults([]);
    if (!trackForm.orderId || !trackForm.phone) {
      setTrackError('Order ID and phone are required.');
      return;
    }
    try {
      setIsTrackingOrder(true);
      const data = await trackOrder(trackForm.orderId, trackForm.phone);
      setTrackResult(data);
    } catch (err) {
      setTrackError(getErrorMessage(err, 'Failed to track order.'));
    } finally {
      setIsTrackingOrder(false);
    }
  }, [trackForm]);

  const handleOrderHistory = useCallback(async () => {
    setTrackError('');
    setTrackResult(null);
    setHistoryResults([]);
    if (!trackForm.phone) {
      setTrackError('Phone number is required for history lookup.');
      return;
    }
    try {
      setIsLoadingHistory(true);
      const data = await fetchOrderHistory(trackForm.phone);
      setHistoryResults(Array.isArray(data) ? data : []);
    } catch (err) {
      setTrackError(getErrorMessage(err, 'Failed to load order history.'));
    } finally {
      setIsLoadingHistory(false);
    }
  }, [trackForm]);

  const closeProductDetail = useCallback(() => {
    setSelectedProduct(null);
    setDetailError('');
  }, []);

  if (showAdmin) {
    return (
      <AdminPanel
        authToken={auth.token}
        onAuthExpired={handleAdminAuthExpired}
        onBack={handleLogout}
        onLogout={handleLogout}
        backendUrl={backendUrl}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white safe-area-bottom">
      <div className="sticky top-0 z-50 safe-area-top shadow-[0_4px_20px_-4px_rgba(0,0,0,0.12)]">
        <AnnouncementBar />
        <StoreTopHeader
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          cartCount={cart.count}
          onCartClick={scrollToCart}
          onAdminClick={() => setShowEmployeePortal(true)}
          searchPlaceholder="Search"
        />
        <StoreMainNav
          categories={CATEGORY_FILTERS}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
          onSaleClick={scrollToFeatured}
        />
      </div>

      <main className="pb-8 sm:pb-12">
        <div className="mx-auto max-w-7xl space-y-5 bg-slate-50/80 px-3 py-4 sm:space-y-10 sm:px-6 sm:py-8 lg:px-8">
          <StoreHero onShopClick={scrollToFeatured} />
          <CategoryShowcase
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
            productCounts={productCounts}
          />
          <StorefrontMediaLayout onShopClick={scrollToFeatured} />
        </div>
        <div
          ref={featuredSectionRef}
          id="featured-products"
          className="mx-auto grid max-w-7xl grid-cols-1 gap-5 px-3 pt-5 sm:gap-8 sm:px-6 sm:pt-8 lg:grid-cols-4 lg:px-8 lg:pb-10"
        >
        <section className="lg:col-span-3">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 pb-3 sm:mb-6 sm:pb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900 sm:text-3xl">Featured picks</h2>
              <p className="mt-1 text-sm text-slate-600">
                {searchTerm.trim()
                  ? 'Matches sorted by availability'
                  : selectedCategory === 'All'
                    ? 'Fresh mix from your catalog'
                    : `Showing: ${selectedCategory}`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => refetchProducts({ clearCache: true })}
              disabled={isLoadingProducts || isRefreshingProducts}
              className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 sm:min-h-0"
            >
              {isLoadingProducts || isRefreshingProducts ? 'Refreshing...' : 'Refresh catalog'}
            </button>
          </div>
          {isLoadingProducts && products.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
              <LoadingSpinner size="lg" text="Loading products..." />
              {isLoadingProductsSlow ? (
                <div className="mx-auto mt-4 max-w-lg rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  The catalog server is still waking up. This can happen after a deploy or when Render sleeps. If this stays here, wait a minute and use Refresh catalog.
                </div>
              ) : null}
            </div>
          ) : productsError && products.length === 0 ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 space-y-3">
              <p>{productsError}</p>
              <button
                type="button"
                onClick={() => refetchProducts({ clearCache: true })}
                className="px-4 py-2 rounded-md bg-red-700 text-white text-sm font-medium hover:bg-red-800 min-touch"
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              {productsError && products.length > 0 ? (
                <div
                  className={`mb-4 rounded-lg border p-3 text-sm flex flex-wrap items-center gap-3 ${
                    productsFromCache
                      ? 'bg-amber-50 border-amber-200 text-amber-900'
                      : 'bg-orange-50 border-orange-200 text-orange-900'
                  }`}
                >
                  <span className="flex-1 min-w-[12rem]">{productsError}</span>
                  <button
                    type="button"
                    onClick={() => refetchProducts({ clearCache: true })}
                    className="px-3 py-1.5 rounded-md bg-white border border-current font-medium hover:opacity-90 min-touch"
                  >
                    Refresh now
                  </button>
                </div>
              ) : null}
              {isRefreshingProducts ? (
                <p className="text-sm text-gray-500 mb-3" aria-live="polite">
                  Updating catalog…
                </p>
              ) : null}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-3">
                {featuredProducts.map((product) => (
                  <ProductCard
                    key={product._id}
                    product={product}
                    backendUrl={backendUrl}
                    onAddToCart={addToCart}
                    onShowDetails={openProductDetail}
                  />
                ))}
                {featuredProducts.length === 0 ? (
                  <p className="col-span-full text-center text-slate-500 py-12">
                    No products match your filter. Try &ldquo;All&rdquo; or another category above.
                  </p>
                ) : null}
              </div>
            </>
          )}
        </section>

        <aside id="store-cart-sidebar" className="scroll-mt-40 space-y-4 lg:sticky lg:top-36 lg:self-start">
          <Cart items={cart.items} onUpdateQty={updateCartQty} />
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <CheckoutForm
              form={checkoutForm}
              onFormChange={setCheckoutForm}
              onPlaceOrder={handlePlaceOrder}
              isPlacingOrder={isPlacingOrder}
              cartEmpty={cart.items.length === 0}
              error={checkoutError}
              success={checkoutSuccess}
              onLookupCustomer={(phone) => lookupCustomerByPhone(phone)}
            />
            <OrderTracking
              trackForm={trackForm}
              onTrackFormChange={setTrackForm}
              onTrack={handleTrackOrder}
              onHistory={handleOrderHistory}
              isTrackingOrder={isTrackingOrder}
              isLoadingHistory={isLoadingHistory}
              trackError={trackError}
              trackResult={trackResult}
              historyResults={historyResults}
            />
          </div>
        </aside>
        </div>
      </main>

      <FloatingChatButton />

      <ProductDetailModal
        product={selectedProduct}
        backendUrl={backendUrl}
        isLoading={isDetailLoading}
        error={detailError}
        onAddToCart={addToCart}
        onClose={closeProductDetail}
      />

      <LoginModal
        visible={showLogin}
        username={loginForm.username}
        password={loginForm.password}
        onUsernameChange={(v) => setLoginForm((c) => ({ ...c, username: v }))}
        onPasswordChange={(v) => setLoginForm((c) => ({ ...c, password: v }))}
        onSubmit={handleLogin}
        onCancel={() => {
          setShowLogin(false);
          setLoginError('');
        }}
        isLoggingIn={isLoggingIn}
        error={loginError}
      />

      <EmployeePortalModal
        visible={showEmployeePortal}
        onClose={() => setShowEmployeePortal(false)}
        onAdminLogin={() => setShowLogin(true)}
      />

      <footer className="border-t border-slate-200 bg-slate-50 text-slate-600 safe-area-bottom">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="font-bold text-slate-800">
                iConnect<span className="text-[#5c2d91]">Store</span>
              </p>
              <p className="mt-2 text-sm">
                © {new Date().getFullYear()} All rights reserved.
              </p>
            </div>
            <ul className="flex flex-wrap gap-x-8 gap-y-3 text-xs font-bold uppercase tracking-wider text-slate-500">
              <li className="text-[#5c2d91]">Secure checkout</li>
              <li className="text-[#8cc63f]">Order tracking</li>
              <li>POS · Reports</li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}
